import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { authApi } from '../../api/auth'
import { trustedDaysLeft } from '../../utils/twoFactor'
import { describeUserAgent } from './SessionsSection'

/**
 * Доверенные устройства (6.1): на них 30 дней не спрашивается код 2FA.
 * Пользователь может снять доверие с любого устройства или со всех сразу.
 */
export function TrustedDevicesSection() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const devices = useQuery({
    queryKey: ['auth', 'trusted-devices'],
    queryFn: () => authApi.listTrustedDevices(),
  })
  const invalidate = () => qc.invalidateQueries({ queryKey: ['auth', 'trusted-devices'] })

  const revoke = useMutation({
    mutationFn: (id: string) => authApi.revokeTrustedDevice(id),
    onSuccess: invalidate,
    onError: (e) => setError(String(e)),
  })
  const revokeAll = useMutation({
    mutationFn: () => authApi.revokeAllTrustedDevices(),
    onSuccess: invalidate,
    onError: (e) => setError(String(e)),
  })

  if (devices.isLoading) return <div className="text-sm text-gray-500">{t('common.loading')}</div>
  const list = devices.data ?? []

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">{t('trustedDevices.intro')}</p>
      {error && (
        <div role="alert" className="bg-red-50 border border-red-200 text-red-700 p-2 rounded text-sm">{error}</div>
      )}
      {list.length === 0 && <div className="text-sm text-gray-500">{t('trustedDevices.empty')}</div>}
      <div className="space-y-2">
        {list.map((d) => (
          <div key={d.id} className="flex items-start justify-between gap-3 p-3 bg-gray-50 border border-gray-200 rounded">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-sm font-medium">
                <span>{describeUserAgent(d.userAgent ?? '', t)}</span>
                {d.isCurrent && (
                  <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                    {t('trustedDevices.thisDevice')}
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {d.ipAddr ?? '—'} · {t('trustedDevices.daysLeft', { n: trustedDaysLeft(d.expiresAt) })}
              </div>
            </div>
            <button
              type="button"
              onClick={() => revoke.mutate(d.id)}
              disabled={revoke.isPending}
              className="text-xs text-red-600 hover:underline shrink-0"
            >
              {t('trustedDevices.revoke')}
            </button>
          </div>
        ))}
      </div>
      {list.length > 1 && (
        <button
          type="button"
          onClick={() => revokeAll.mutate()}
          disabled={revokeAll.isPending}
          className="text-sm text-red-600 hover:underline"
        >
          {t('trustedDevices.revokeAll', { n: list.length })}
        </button>
      )}
    </div>
  )
}
